import { Button } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { DeviceCardProps } from '../DeviceCardProps';
import { removeDriveBaseSettingsAssignments } from '../DeviceCardBuilder';
import DriveTypeControl from '../Controls/DriveTypeControl';
import ReferenceFrameControl from '../Controls/ReferenceFrameControl';
import ControlStickDriveStyleControl from '../Controls/ControlStickDriveStyleControl';
import InputControl from '../Controls/InputControl';
import SliderControl from '../Controls/SliderControl';
import DeviceCardWrapper from '../DeviceCardWrapper';
import { DriveBaseMechanismIcon } from './mechanismIcons';
import { useControlInitialization } from '../../../../hooks/controls/useControlInitialization';
import { SKID_MODE_UI_ENABLED } from '../../../../utils/constants';
import { isUnsupportedEnumRawValue, parseKnownEnumRawValue } from '../Controls/unsupportedEnumValue';

enum DriveType {
  SWERVE = 'DriveType.SWERVE',
  SKID_STEER = 'DriveType.SKID_STEER',
}

const DRIVE_TYPE_VALUES = [DriveType.SWERVE, DriveType.SKID_STEER] as const;

const WHEEL_DIAMETER_MARKS = [50, 60, 70, 80, 90, 100].map((value) => ({
  value,
}));

const DriveBaseCard = (props: DeviceCardProps) => {
  const { t } = useTranslation();
  const { value: driveType } = useControlInitialization<DriveType>(
    { ...props, parameterName: 'drive_type' },
    {
      parseValue: (v) => parseKnownEnumRawValue(v, DRIVE_TYPE_VALUES, DriveType.SWERVE),
      isUnsupportedRawValue: (rawValue) => isUnsupportedEnumRawValue(rawValue, DRIVE_TYPE_VALUES),
    }
  );
  const isSkidSteer = driveType === DriveType.SKID_STEER;

  return (
    <DeviceCardWrapper header={props.header} headerIcon={DriveBaseMechanismIcon} initialOpen={props.initialOpen} onDelete={props.onDelete}>
      {SKID_MODE_UI_ENABLED && (
        <DriveTypeControl
          {...props}
          parameterName="drive_type"
          label={<Trans i18nKey="driveTypeControl.drive_type">Drive type</Trans>}
          labelId="drive-type-label"
          labelPopoverId="drive-type-popover"
          labelPopover={t('driveTypeControl.drive_type_info_tooltip')}
          selectId="drive-type"
        />
      )}
      <ReferenceFrameControl
        {...props}
        parameterName="reference_frame"
        label={<Trans i18nKey="referenceFrameControl.reference_frame">Reference Frame</Trans>}
        labelId="reference-frame-label"
        labelPopoverId="reference-frame-popover"
        labelPopover={t('referenceFrameControl.reference_frame_info_tooltip')}
        selectId="reference-frame"
      />
      <InputControl
        {...props}
        parameterName="max_translation_speed"
        label={<Trans i18nKey="driveBaseCard.max_translation_speed">Maximum Speed</Trans>}
        labelId="max-translation-speed"
        labelPopoverId="max-translation-speed-popover"
        labelPopover={t('driveBaseCard.max_translation_speed_info_tooltip')}
        step={0.01}
        min={0.01}
        max={1.4}
        unitLabel="m/s"
      />
      <InputControl
        {...props}
        parameterName="max_rotation_speed"
        label={<Trans i18nKey="driveBaseCard.max_rotation_speed">Maximum Rotational Speed</Trans>}
        labelId="max-rotation-speed"
        labelPopoverId="max-rotation-speed-popover"
        labelPopover={t('driveBaseCard.max_rotation_speed_info_tooltip')}
        step={1}
        min={10}
        max={360}
        unitLabel="°/s"
      />
      {isSkidSteer && (
        <SliderControl
          {...props}
          parameterName="wheel_diameter"
          label={<Trans i18nKey="driveBaseCard.wheel_diameter">Wheel Diameter</Trans>}
          labelId="wheel-diameter"
          labelPopoverId="wheel-diameter-popover"
          labelPopover={t('driveBaseCard.wheel_diameter_info_tooltip')}
          labelUnits="mm"
          step={1}
          min={50}
          max={100}
          marks={WHEEL_DIAMETER_MARKS}
        />
      )}
      <ControlStickDriveStyleControl
        {...props}
        parameterName="control_stick_drive_style"
        label={<Trans i18nKey="controlStickDriveStyleControl.control_stick_style">Control Stick Style</Trans>}
        labelId="control-stick-style-label"
        labelPopoverId="control-stick-style-popover"
        labelPopover={t('controlStickDriveStyleControl.control_stick_style_info_tooltip')}
      />
      <Button variant="outlined" onClick={() => removeDriveBaseSettingsAssignments(props)}>
        <Trans i18nKey="driveBaseCard.reset_drive_base_settings">Restore Defaults</Trans>
      </Button>
    </DeviceCardWrapper>
  );
};

export default DriveBaseCard;
